import * as React from 'react'
import { ArrowRight, Image as ImageIcon, Layers } from 'lucide-react'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import type { StoryboardSummary } from '@/types'

/** Render the storyboard's palette swatches (Stage A plan often emits 3-6
 *  colour entries — either hex strings or named tones). Gracefully degrades
 *  when palette is null, empty, or a non-array. */
function PaletteSwatches({ palette }: { palette: unknown }) {
  const hexes = React.useMemo<string[]>(() => {
    if (!Array.isArray(palette)) return []
    return palette
      .map((entry) => {
        if (typeof entry === 'string') return entry
        if (entry && typeof entry === 'object' && 'hex' in entry) {
          return String((entry as { hex: unknown }).hex)
        }
        return null
      })
      .filter((v): v is string => typeof v === 'string')
      .slice(0, 6)
  }, [palette])

  if (hexes.length === 0) return null
  return (
    <div className="flex items-center gap-0.5" aria-label="Storyboard palette">
      {hexes.map((c, i) => (
        <span
          key={i}
          className="h-3 w-3 rounded-sm border border-border/50"
          style={{ background: c }}
          title={c}
        />
      ))}
    </div>
  )
}

interface StoryboardCardProps {
  storyboard: StoryboardSummary
  onClick: () => void
  /** "Use in Create Ad" handler — routes user to the wizard pre-loaded
   *  with this storyboard's primary asset. */
  onUseInCreate: () => void
}

export function StoryboardCard({
  storyboard,
  onClick,
  onUseInCreate,
}: StoryboardCardProps) {
  const primary = storyboard.primary_asset
  const thumbnailUrl = primary?.file_path
    ? `/uploads/${primary.file_path}`
    : null

  return (
    <div
      className={cn(
        'group flex flex-col rounded-lg border border-border bg-card overflow-hidden',
        'transition-colors hover:border-foreground/40 cursor-pointer',
      )}
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onClick()
        }
      }}
    >
      {/* Thumbnail — multi-panel storyboard images are usually wide, so use
          aspect-video to fit them properly without cropping. Fallback to an
          icon if the primary asset is missing (rare — only if user deleted
          the rendered image but kept the storyboard parent row). */}
      <div className="relative aspect-video w-full bg-muted overflow-hidden shrink-0">
        {thumbnailUrl ? (
          <img
            src={thumbnailUrl}
            alt={storyboard.title ?? 'Storyboard'}
            className="w-full h-full object-cover"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-muted-foreground">
            <ImageIcon className="h-8 w-8 opacity-40" />
          </div>
        )}
        {/* Panel-count badge top-right */}
        {storyboard.panel_count != null && (
          <Badge
            variant="secondary"
            className="absolute top-2 right-2 text-[10px] gap-1 backdrop-blur-sm bg-background/80"
          >
            <Layers className="h-3 w-3" />
            {storyboard.panel_count} panels
          </Badge>
        )}
      </div>

      {/* Body */}
      <div className="flex flex-col flex-1 p-3 gap-2 min-w-0">
        <div className="flex items-start gap-2 min-w-0">
          <h3 className="text-sm font-medium truncate flex-1">
            {storyboard.title || 'Untitled storyboard'}
          </h3>
          <PaletteSwatches palette={storyboard.palette} />
        </div>
        {storyboard.brief && (
          <p className="text-xs text-muted-foreground line-clamp-2">
            {storyboard.brief}
          </p>
        )}
        <div className="flex items-center justify-between gap-2 mt-auto">
          <span className="text-[10px] text-muted-foreground tabular-nums">
            {storyboard.asset_count} asset{storyboard.asset_count !== 1 ? 's' : ''}
          </span>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-7 text-xs gap-1"
            onClick={(e) => {
              e.stopPropagation()
              onUseInCreate()
            }}
          >
            Use in Create Ad
            <ArrowRight className="h-3 w-3" />
          </Button>
        </div>
      </div>
    </div>
  )
}
